import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from '../../../../../ui/toast';
import employeeIllustration from '../../../../../assets/images/backgrounds/employee_performance.png';
import candidateIllustration from '../../../../../assets/images/backgrounds/job_candidate.png';
import partnerIllustration from '../../../../../assets/images/backgrounds/partner_performance.png';
import { Button } from '../../../../../ui';
import { CardIcon } from '../../../../../ui/icons/icons';
import {
  PrimaryTypography,
  SecondaryTypography,
} from '../../../../../ui/typography/typography';
import {
  addSubscription,
  cancelSubscription,
  getStripePortalLink,
  getSubscriptionProducts,
} from '../../../../../utils/api_service/subscription_api';
import { FeatureNames } from '../../../../../utils/helpers/subscriptionEnum';
import { fetchSubscriptions } from '../../../../../utils/redux_store/features/subscription_reducer';
import { RootState, store } from '../../../../../utils/redux_store/store';
import { Product } from '../../../../../utils/types/admin_types';
import { getCredits } from '../../../../../utils/api_service/credits_api';
import { UsageMeter } from '../../../../../ui/usage_meter/UsageMeter';
import { CANDI_BANK_CV, FeatureCredits, creditTone, formatResetDate } from '../../../../../utils/types/credits_types';
import { FeatureCard } from './FeatureCard';

/** Which license cards show a monthly credit meter, and which feature feeds it. */
const LICENSE_CREDIT_FEATURE: Partial<Record<string, string>> = {
  hierTalentCandiBank: CANDI_BANK_CV,
};

const featureDescriptions = {
  candidateDescription:
    'Automatically assess any skills of any candidate at any time. Easily compare candidate results and automatically short list the top contenders.',
  employeeDescription:
    'Continuously assess employees to keep their skills sharp. Compare employees within teams, and compare the skills of teams and departments. Identify high and low achievers, as well as skill gaps that needs attention.',
  partnerDescription:
    'Ensure you select only the best contractors to work for you. Assess all potential partners and compare employees between different partners in order to make the best fully informed decision.',
};

export const Subscription = () => {
  const subscription = useSelector(
    (state: RootState) => state.subscriptions.subscriptions,
  );

  const [productsList, setProductsList] = useState<Product[]>();
  const [credits, setCredits] = useState<Record<string, FeatureCredits>>({});
  const [creditsError, setCreditsError] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(false);

  const hierTalentProducts = [
    {
      key: 'hierTalentBase' as const,
      label: 'HierTalent Base',
      features: ['Applicant Analysis'],
      color: 'emerald',
    },
    {
      key: 'hierTalentAutoClassify' as const,
      label: 'HierTalent Auto Classify',
      features: ['Auto Suitability', 'Auto Outstanding'],
      color: 'indigo',
    },
    {
      key: 'hierTalentJobPost' as const,
      label: 'HierTalent Job Post',
      features: ['Job Posting'],
      color: 'amber',
    },
    {
      key: 'hierTalentAutoAssessments' as const,
      label: 'HierTalent Auto Assessments',
      features: ['Automated Assessment Management', 'Auto Scoring'],
      color: 'blue',
    },
    {
      key: 'hierTalentSmartInterviews' as const,
      label: 'HierTalent Smart Interviews',
      features: ['Recorded Interviews', 'AI Analysis & Scoring'],
      color: 'rose',
    },
    {
      key: 'hierTalentCandiBank' as const,
      label: 'HierTalent CandiBank',
      features: ['Candidate Bank Access'],
      color: 'slate',
    },
    {
      key: 'hierTalentCca' as const,
      label: 'HierTalent CCA',
      features: ['Candidate Competency Analysis'],
      color: 'violet',
    },
    {
      key: 'hierTalentPsychometricFaxtor' as const,
      label: 'HierTalent Psychometric Faxtor',
      features: ['Psychometric Analytics'],
      color: 'purple',
    },
    {
      key: 'hierTalentReporting' as const,
      label: 'HierTalent Reporting',
      features: ['Reporting'],
      color: 'cyan',
    },
    {
      key: 'hierTalentWellness' as const,
      label: 'HierTalent Wellness',
      features: ['Wellness Screening', 'DASS-21 / GAD-7 / PHQ-9 Assessments'],
      color: 'teal',
    },
  ];

  const unixToDisplay = (unix: number | string): string =>
    new Date(Number(unix) * 1000).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const isLicenseActive = (feature?: { startDate: number | string; endDate: number | string }): boolean => {
    if (!feature) return false;
    return Number(feature.endDate) * 1000 > Date.now();
  };

  useEffect(() => {
    // Only tenants holding a metered license need the meter; skip the call otherwise.
    const metered = Object.keys(LICENSE_CREDIT_FEATURE).some((key) => !!subscription?.[key as keyof typeof subscription]);
    if (!metered) return;
    let cancelled = false;
    setCreditsError(false);
    getCredits()
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setCreditsError(true);
          return;
        }
        const data = await res.json();
        const byFeature: Record<string, FeatureCredits> = {};
        (data.credits ?? []).forEach((c: FeatureCredits) => { byFeature[c.feature] = c; });
        setCredits(byFeature);
      })
      .catch(() => {
        if (!cancelled) setCreditsError(true);
      });
    return () => { cancelled = true; };
  }, [subscription]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getSubscriptionProducts();
        if (response.ok) {
          const data = await response.json();
          setProductsList(Object.values(data));
        } else {
          let errorMessage = 'Failed to load subscription products';
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch {
            // Response body was not JSON (e.g. HTML 500 error page)
          }
          toast.error(errorMessage, { position: 'bottom-center' });
          setProductsError(true);
        }
      } catch (error) {
        toast.error('Error fetching products: ' + error, {
          position: 'bottom-center',
        });
        setProductsError(true);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const getSubscriptionObject = (feature: string) => {
    if (subscription) {
      switch (feature) {
        case FeatureNames.CANDIDATE:
          return subscription.candidateFeature;
        case FeatureNames.EMPLOYEE:
          return subscription.employeeFeature;
        case FeatureNames.PARTNER:
          return subscription.partnerFeature;
      }
    }
  };

  const getTrialStatus = (feature: string) => {
    if (subscription) {
      switch (feature) {
        case FeatureNames.CANDIDATE:
          return subscription.trial.candidateFeature;
        case FeatureNames.EMPLOYEE:
          return subscription.trial.employeeFeature;
        case FeatureNames.PARTNER:
          return subscription.trial.partnerFeature;
        default:
          return true;
      }
    }
    return true;
  };

  const getImage = (feature: string) => {
    switch (feature) {
      case FeatureNames.CANDIDATE:
        return candidateIllustration;
      case FeatureNames.EMPLOYEE:
        return employeeIllustration;
      case FeatureNames.PARTNER:
        return partnerIllustration;
      default:
        return candidateIllustration;
    }
  };

  const getDescriptions = (feature: string) => {
    switch (feature) {
      case FeatureNames.CANDIDATE:
        return featureDescriptions.candidateDescription;
      case FeatureNames.EMPLOYEE:
        return featureDescriptions.employeeDescription;
      case FeatureNames.PARTNER:
        return featureDescriptions.partnerDescription;
      default:
        return featureDescriptions.candidateDescription;
    }
  };

  const createSubscription = async (priceId: string, trial: boolean) => {
    try {
      const response = await addSubscription(priceId, trial);
      if (response.ok) {
        toast.success(trial ? 'Trial Started' : 'Successfully subscribed', {
          position: 'bottom-center',
        });
        store.dispatch(fetchSubscriptions());
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error subscribing: ' + error, { position: 'bottom-center' });
    }
  };

  const deleteSubscription = async (featureType: string, cancel: boolean) => {
    try {
      const response = await cancelSubscription(featureType, cancel);
      if (response.ok) {
        toast.success(`${cancel ? 'Removed' : 'Renewed'} Subscription`, {
          position: 'bottom-center',
        });
        store.dispatch(fetchSubscriptions());
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error removing subscribing: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const accessStripePortal = async () => {
    try {
      const response = await getStripePortalLink();
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.portal_url;
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error subscribing: ' + error, { position: 'bottom-center' });
    }
  };

  return (
    <>
      <div className='container mx-auto px-4 py-8 max-w-[1400px]'>
        <div className='flex flex-col md:flex-row gap-8 w-full'>
          { /* Feature Cards Grid */ }
          <div className='flex-1 min-w-0'>
            { productsLoading ? (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                { [1, 2, 3].map((i) => (
                  <div key={i} className='bg-white rounded-2xl border border-slate-200 shadow-sm p-6 animate-pulse'>
                    <div className='h-4 bg-slate-200 rounded w-3/4 mb-4' />
                    <div className='h-24 bg-slate-100 rounded mb-4' />
                    <div className='h-8 bg-slate-200 rounded' />
                  </div>
                )) }
              </div>
            ) : productsError ? (
              <div className='flex flex-col items-center justify-center py-20 text-center'>
                <svg className='h-12 w-12 text-slate-300 mb-3' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z' />
                </svg>
                <p className='text-sm font-semibold text-slate-500'>Unable to load subscription plans</p>
                <p className='text-xs text-slate-400 mt-1'>The products service is temporarily unavailable. Your active licenses are shown below.</p>
              </div>
            ) : (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                { productsList?.map((product: Product) => (
                  <FeatureCard
                      key={product.productId}
                      product={product}
                      companySize={subscription?.companySize || 'Small'}
                      interval={subscription?.interval || 'month'}
                      currentSubscription={getSubscriptionObject(product.feature)}
                      img={getImage(product.feature)}
                      description={getDescriptions(product.feature)}
                      isTrialUsed={getTrialStatus(product.feature)}
                      cardDetailsUnavailable={
                        subscription?.defaultCard === undefined
                      }
                      addSubscription={createSubscription}
                      cancelSubscription={deleteSubscription}
                  />
                )) }
              </div>
            ) }
          </div>

          { /* Sidebar */ }

          <div className='w-full md:w-[340px] flex-shrink-0 flex flex-col gap-5'>
            { /* Payment Card */ }
            <div className='bg-white rounded-2xl shadow-lg border border-gray-100 px-5 py-6'>
              <div className='text-lg font-semibold text-gray-900 mb-3'>
                Payment Details
              </div>
              <div className='flex items-center gap-4 mb-3 flex-wrap'>
                { subscription && subscription?.defaultCard ? (
                  <>
                    <div className='flex items-center gap-2'>
                      <CardIcon
                          strokeColor={
                            subscription?.defaultCard.brand === 'Visa'
                              ? '#29AAE2'
                              : '#D72A27'
                          }
                      />
                      <span className='text-gray-800 font-medium'>
                        { subscription?.defaultCard.brand }
                      </span>
                    </div>
                    <div className='flex flex-col text-xs text-gray-600'>
                      <span>
                        Ending in{ ' ' }
                        <span className='font-semibold'>
                          { subscription?.defaultCard.last4 }
                        </span>
                      </span>
                      <span>
                        Expires{ ' ' }
                        <span className='font-semibold'>
                          { subscription?.defaultCard.expiryDate }
                        </span>
                      </span>
                    </div>
                  </>
                ) : (
                  <span className='text-sm text-gray-500'>
                    No card details available
                  </span>
                ) }
                <Button
                    variant='primary'
                    label='Manage'
                    onClick={() => accessStripePortal()}
                />
              </div>
            </div>

            { /* Policy Card */ }
            <div className='bg-slate-50 rounded-2xl border border-gray-100 px-5 py-5'>
              <div className='text-lg font-semibold text-gray-900 mb-2'>
                Policy
              </div>
              <div className='text-sm text-gray-700 mb-1'>
                <span className='font-medium'>Company Size:</span>{ ' ' }
                <span className='font-semibold'>
                  { subscription?.companySize ?? '--' }
                </span>
              </div>
              <div className='text-sm text-gray-700'>
                <span className='font-medium'>Billing Cycle:</span>{ ' ' }
                <span className='font-semibold capitalize'>
                  { subscription?.interval
                    ? subscription.interval.charAt(0).toUpperCase() +
                      subscription.interval.slice(1)
                    : '--' }
                </span>
              </div>
            </div>
          </div>
        </div>

        { /* HierTalent Licenses */ }
        <div className='mt-10'>
          <h2 className='text-xl font-bold text-slate-800 mb-6 tracking-tight'>Platform Licenses</h2>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
            { hierTalentProducts.map(({ key, label, features, color }) => {
              const license = subscription?.[key];
              const active = isLicenseActive(license);

              return (
                <div
                    key={key}
                    className={`bg-white rounded-2xl border shadow-md p-6 flex flex-col gap-4 transition-all ${
                      active ? 'border-slate-200' : 'border-slate-200 opacity-50 grayscale'
                    }`}
                >
                  { /* Header */ }
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-black text-slate-800 tracking-tight'>{ label }</span>
                    <span
                        className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                          active
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                    >
                      { active ? 'Active' : license ? 'Expired' : 'Not Licensed' }
                    </span>
                  </div>

                  { /* Dates */ }
                  { license ? (
                    <div className='grid grid-cols-2 gap-3'>
                      <div className='bg-slate-50 rounded-xl p-3'>
                        <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1'>Start Date</p>
                        <p className='text-sm font-bold text-slate-700'>{ unixToDisplay(license.startDate) }</p>
                      </div>
                      <div className='bg-slate-50 rounded-xl p-3'>
                        <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1'>End Date</p>
                        <p className={`text-sm font-bold ${active ? 'text-slate-700' : 'text-rose-500'}`}>
                          { unixToDisplay(license.endDate) }
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className='bg-slate-50 rounded-xl p-3 text-center'>
                      <p className='text-xs text-slate-400 font-medium'>No license assigned</p>
                    </div>
                  ) }

                  { /* Monthly credits (metered licenses only) */ }
                  { license && LICENSE_CREDIT_FEATURE[key] && (() => {
                    const c = credits[LICENSE_CREDIT_FEATURE[key] as string];
                    const limit = license.monthlyCredits ?? null;
                    const tone = c ? creditTone(c) : 'ok';
                    return (
                      <div className='bg-slate-50 rounded-xl p-3' data-testid='license-credits'>
                        <div className='flex items-center justify-between mb-1'>
                          <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                            Credits / month
                          </p>
                          <p className='text-xs font-bold text-slate-700 tabular-nums'>
                            { limit === null ? 'Unlimited' : `${limit.toLocaleString()} ${c?.unit ?? 'CVs'}` }
                          </p>
                        </div>
                        { c ? (
                          <UsageMeter
                              used={c.used}
                              limit={c.monthlyLimit}
                              tone={tone}
                              leftLabel={c.unlimited ? `${c.used.toLocaleString()} used this month` : `${c.used.toLocaleString()} used · ${(c.remaining ?? 0).toLocaleString()} left`}
                              rightLabel={`resets ${formatResetDate(c)}`}
                          />
                        ) : creditsError ? (
                          <p className='text-[11px] text-rose-500 font-medium'>Usage unavailable right now</p>
                        ) : (
                          <p className='text-[11px] text-slate-400 font-medium'>Usage loading…</p>
                        ) }
                      </div>
                    );
                  })() }

                  { /* Features */ }
                  <div className='flex flex-col gap-1.5'>
                    <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>Includes</p>
                    { features.map((feature) => (
                      <div key={feature} className='flex items-center gap-2'>
                        <div className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <span className='text-xs font-semibold text-slate-600'>{ feature }</span>
                      </div>
                    )) }
                  </div>
                </div>
              );
            }) }
          </div>
        </div>

        { /* Note */ }
        <div className='mt-8 text-center'>
          <span className='text-xs font-semibold text-gray-400 tracking-wide'>
            NOTE: To change any policies, please contact{ ' ' }
            <span className='underline text-primary-main'>
              sales@hiacuity.com
            </span>
          </span>
        </div>
      </div>
    </>
  );
};
